'use client'

import { cn } from '@brew/ui/cn'
import { type MouseEvent, useRef, useState } from 'react'
import { AwardBadgeIcon } from '@/components/marketing/icons/award-badge-icon'

type AwardBadgeType =
  | 'golden-kitty'
  | 'product-of-the-day'
  | 'product-of-the-month'
  | 'product-of-the-week'

type AwardBadgeTone = 'gold' | 'silver' | 'bronze'

interface AwardBadgeProps {
  type: AwardBadgeType
  place?: number
  link?: string
  className?: string
  /**
   * Optional Product Hunt topic name (e.g. "Design Tools",
   * "Artificial Intelligence"). When provided, the eyebrow text reads
   * `PRODUCT HUNT · {CATEGORY}` so the badge can be honest about being a
   * topic-scoped win rather than an overall one.
   */
  category?: string
  /**
   * Optional override for the badge background color. When omitted, the
   * tone is inferred from `place` (1 → gold, 2 → silver, 3 → bronze).
   * Pass `gold` to keep the gold look on a topic-scoped #3 finish.
   */
  tone?: AwardBadgeTone
}

const TONE_BG: Record<AwardBadgeTone, string> = {
  gold: '#f3e3ac',
  silver: '#ddd',
  bronze: '#f1cfa6',
}

const backgroundColor = [TONE_BG.gold, TONE_BG.silver, TONE_BG.bronze]

const title: Record<AwardBadgeType, string> = {
  'golden-kitty': 'Golden Kitty Awards',
  'product-of-the-day': 'Product of the Day',
  'product-of-the-month': 'Product of the Month',
  'product-of-the-week': 'Product of the Week',
}

export function AwardBadge({
  type,
  place,
  link,
  className,
  category,
  tone,
}: AwardBadgeProps) {
  const ref = useRef<HTMLAnchorElement>(null)
  const [firstOverlayPosition, setFirstOverlayPosition] = useState<number>(0)
  const [isInOutOverlayAnimationDisabled, setIsInOutOverlayAnimationDisabled] =
    useState<boolean>(true)
  const [isOverlayAnimationDisabled, setIsOverlayAnimationDisabled] =
    useState<boolean>(false)
  const enterTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimeout1 = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimeout2 = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimeout3 = useRef<ReturnType<typeof setTimeout> | null>(null)

  const getDimensions = () => {
    const rect = ref.current?.getBoundingClientRect()
    return {
      left: rect?.left ?? 0,
      right: rect?.right ?? 0,
      top: rect?.top ?? 0,
      bottom: rect?.bottom ?? 0,
    }
  }

  const computeCursorOffset = (clientX: number, clientY: number) => {
    const { left, right, top, bottom } = getDimensions()
    const xCenter = (left + right) / 2
    const yCenter = (top + bottom) / 2
    return (Math.abs(xCenter - clientX) + Math.abs(yCenter - clientY)) / 6
  }

  const handleMouseEnter = (e: MouseEvent<HTMLAnchorElement>) => {
    if (leaveTimeout1.current) {
      clearTimeout(leaveTimeout1.current)
    }
    if (leaveTimeout2.current) {
      clearTimeout(leaveTimeout2.current)
    }
    if (leaveTimeout3.current) {
      clearTimeout(leaveTimeout3.current)
    }
    setIsOverlayAnimationDisabled(true)

    setIsInOutOverlayAnimationDisabled(false)
    enterTimeout.current = setTimeout(
      () => setIsInOutOverlayAnimationDisabled(true),
      350
    )
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setFirstOverlayPosition(computeCursorOffset(e.clientX, e.clientY))
      })
    })
  }

  const handleMouseMove = (e: MouseEvent<HTMLAnchorElement>) => {
    setTimeout(
      () => setFirstOverlayPosition(computeCursorOffset(e.clientX, e.clientY)),
      150
    )
  }

  const handleMouseLeave = () => {
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current)
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsInOutOverlayAnimationDisabled(false)
        leaveTimeout1.current = setTimeout(
          () => setFirstOverlayPosition(-firstOverlayPosition / 4),
          150
        )
        leaveTimeout2.current = setTimeout(
          () => setFirstOverlayPosition(0),
          300
        )
        leaveTimeout3.current = setTimeout(() => {
          setIsOverlayAnimationDisabled(false)
          setIsInOutOverlayAnimationDisabled(true)
        }, 500)
      })
    })
  }

  const eyebrowText = category
    ? `PRODUCT HUNT · ${category.toUpperCase()}`
    : 'PRODUCT HUNT'
  const eyebrowFontSize = eyebrowText.length > 20 ? 7.5 : 9
  const fillColor = tone
    ? TONE_BG[tone]
    : backgroundColor[(place || 2) - 1] || backgroundColor[1]
  const ariaLabel = [
    'Product Hunt:',
    title[type],
    place ? `#${place}` : '',
    category ? `in ${category}` : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <a
      ref={ref}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('block h-auto w-[180px] sm:w-[260px]', className)}
      aria-label={ariaLabel}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <AwardBadgeIcon
        fillColor={fillColor}
        eyebrowText={eyebrowText}
        eyebrowFontSize={eyebrowFontSize}
        label={`${title[type]}${place ? ` #${place}` : ''}`}
        overlayPosition={firstOverlayPosition}
        isOverlayTransitionEnabled={!isInOutOverlayAnimationDisabled}
        isOverlayAnimationEnabled={!isOverlayAnimationDisabled}
      />
    </a>
  )
}
